import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ChartLine, Users, Compass, Wrench, ChatTeardropDots, Calendar, Robot,
  ArrowsClockwise, Funnel, FunnelSimple, Download, ClipboardText, MagnifyingGlass, Table as TableIcon
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_LABELS = {
  wealth_health_score: 'Wealth Health Score™',
  retirement_gap_analyzer: 'Retirement Gap Analyzer',
  roth_conversion: 'Roth Conversion',
  social_security: 'Social Security',
  scenario_planner: 'Scenario Planner',
  journey_timeline: 'Journey Timeline',
  advisor_dashboard: 'Advisor Dashboard',
  household: 'Household',
};

const Stat = ({ label, value, sub, accent = false, testid }) => (
  <div
    className={`rounded-md p-5 border ${accent ? 'bg-[#0A1128] text-white border-[#0A1128]' : 'bg-white border-slate-200'}`}
    data-testid={testid}
  >
    <p className={`text-xs font-semibold uppercase tracking-[0.1em] mb-2 ${accent ? 'text-[#C5A059]' : 'text-[#4A5568]'}`}>{label}</p>
    <p className={`text-2xl font-semibold ibm-plex ${accent ? 'text-white' : 'text-[#0A1128]'}`}>{value}</p>
    {sub && <p className={`text-xs mt-1 ${accent ? 'text-[#A0AABF]' : 'text-[#A0AABF]'}`}>{sub}</p>}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <Icon size={20} weight="duotone" className="text-[#C5A059]" />
      <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">{title}</h2>
    </div>
    {children}
  </div>
);

const ToolList = ({ tools }) => (
  <ul className="space-y-2">
    {tools.length === 0 && <li className="text-sm text-[#A0AABF]">No data yet</li>}
    {tools.map((t) => (
      <li key={t.tool} className="flex justify-between text-sm">
        <span className="text-[#1A202C]">{TOOL_LABELS[t.tool] || t.tool}</span>
        <span className="ibm-plex font-semibold text-[#0A1128]">
          {t.unique_users} <span className="text-[#A0AABF] font-normal">users · {t.total_uses} uses</span>
        </span>
      </li>
    ))}
  </ul>
);

const AdminAnalytics = () => {
  const [adminKey, setAdminKey] = useState(() => new URLSearchParams(window.location.search).get('admin_key') || '');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Beta program state
  const [betaCohort, setBetaCohort] = useState('all'); // 'all' | 'phase_1' | 'phase_2'
  const [betaFunnel, setBetaFunnel] = useState(null);
  const [betaParticipants, setBetaParticipants] = useState([]);
  const [betaAggregate, setBetaAggregate] = useState(null);
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaError, setBetaError] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantSort, setParticipantSort] = useState('registered_at_desc');
  const [participantEvalFilter, setParticipantEvalFilter] = useState('all'); // all|completed|not_completed
  const [participantOnbFilter, setParticipantOnbFilter] = useState('all'); // all|completed|pending

  const load = async (key, windowDays) => {
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`, { params: { admin_key: key, days: windowDays } }),
        axios.get(`${API}/admin/analytics/funnel`, { params: { admin_key: key } }),
      ]);
      setData(summaryRes.data);
      setFunnel(funnelRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load analytics');
      setData(null);
      setFunnel(null);
    } finally {
      setLoading(false);
    }
  };

  const loadBeta = async (key, cohort) => {
    if (!key) return;
    setBetaLoading(true);
    setBetaError(null);
    try {
      const params = { admin_key: key };
      const cohortParam = cohort && cohort !== 'all' ? { ...params, cohort } : params;
      const [f, p, a] = await Promise.all([
        axios.get(`${API}/admin/analytics/beta-funnel`, { params: cohortParam }),
        axios.get(`${API}/admin/beta-participants`, { params: cohortParam }),
        axios.get(`${API}/admin/beta-evaluation/aggregate`, { params: cohortParam }),
      ]);
      setBetaFunnel(f.data);
      setBetaParticipants(p.data?.participants || []);
      setBetaAggregate(a.data);
    } catch (e) {
      setBetaError(e.response?.data?.detail || 'Failed to load beta analytics');
    } finally {
      setBetaLoading(false);
    }
  };

  const downloadBetaCsv = () => {
    if (!adminKey) return;
    const q = new URLSearchParams({ admin_key: adminKey });
    if (betaCohort !== 'all') q.set('cohort', betaCohort);
    window.open(`${API}/admin/beta-evaluation/export.csv?${q.toString()}`, '_blank');
  };

  useEffect(() => {
    if (adminKey) { load(adminKey, days); loadBeta(adminKey, betaCohort); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload beta when cohort changes
  useEffect(() => {
    if (adminKey) loadBeta(adminKey, betaCohort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betaCohort]);

  const filteredParticipants = React.useMemo(() => {
    let rows = [...betaParticipants];
    const q = participantSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) =>
      (r.email || '').toLowerCase().includes(q) || (r.full_name || '').toLowerCase().includes(q));
    if (participantEvalFilter === 'completed') rows = rows.filter((r) => r.evaluation_completed);
    if (participantEvalFilter === 'not_completed') rows = rows.filter((r) => !r.evaluation_completed);
    if (participantOnbFilter === 'completed') rows = rows.filter((r) => r.modules_visited_count >= 6 && r.scenario_created);
    if (participantOnbFilter === 'pending') rows = rows.filter((r) => !(r.modules_visited_count >= 6 && r.scenario_created));
    rows.sort((a, b) => {
      const [f, dir] = participantSort.split('_').reduce((acc, cur, i, arr) => {
        if (i === arr.length - 1) return [arr.slice(0, -1).join('_'), cur];
        return acc;
      }, ['', '']);
      const av = (a[f] ?? '') + '';
      const bv = (b[f] ?? '') + '';
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return rows;
  }, [betaParticipants, participantSearch, participantEvalFilter, participantOnbFilter, participantSort]);

  return (
    <div className="min-h-screen bg-[#FDFCFB] p-6 md:p-12">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-semibold">PLANIVANCE AI</p>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mt-1">
            Beta Analytics Dashboard
          </h1>
          <p className="text-base text-[#4A5568] mt-2">
            Measured user behavior across the private beta. Admin-key gated; share with caution.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs text-[#4A5568]">Admin Key</Label>
              <Input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Enter ADMIN_KEY"
                data-testid="admin-key-input"
                className="border-slate-300"
              />
            </div>
            <div className="w-32">
              <Label className="text-xs text-[#4A5568]">Window (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(Number(e.target.value) || 30)}
                data-testid="admin-window-input"
                className="border-slate-300"
              />
            </div>
            <Button
              onClick={() => load(adminKey, days)}
              disabled={loading || !adminKey}
              data-testid="admin-load-button"
              className="bg-[#0A1128] hover:bg-[#152145]"
            >
              <ArrowsClockwise size={16} className="mr-2" />
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
          {error && <p className="text-sm text-[#C53030] mt-3" data-testid="admin-error">{error}</p>}
        </div>

        {!data ? (
          <div className="bg-white border border-slate-200 rounded-md p-12 text-center text-[#A0AABF]">
            Enter your admin key and refresh to load analytics.
          </div>
        ) : (
          <>
            {/* Drop-Off Funnel */}
            {funnel && (
              <Section icon={Funnel} title="Drop-Off Funnel">
                <div className="bg-white border border-slate-200 rounded-md p-5 dashboard-card" data-testid="funnel-section">
                  <p className="text-sm text-[#4A5568] mb-4">
                    Cumulative beta funnel (all-time). Step retention shown vs the previous stage.
                  </p>
                  <div className="space-y-3">
                    {funnel.stages.map((s, idx) => {
                      const widthPct = funnel.stages[0].users ? (s.users / funnel.stages[0].users * 100) : 0;
                      const stepRet = s.step_retention_pct;
                      return (
                        <div key={s.id} data-testid={`funnel-stage-${s.id}`}>
                          <div className="flex justify-between items-baseline mb-1 flex-wrap gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold ibm-plex text-[#A0AABF]">{idx + 1}.</span>
                              <span className="text-sm font-medium text-[#1A202C]">{s.label}</span>
                              {idx > 0 && s.dropoff_users > 0 && (
                                <span className="text-xs text-[#C53030]">−{s.dropoff_users} dropped</span>
                              )}
                            </div>
                            <div className="text-sm ibm-plex">
                              <span className="font-semibold text-[#0A1128]">{s.users}</span>
                              <span className="text-[#A0AABF]"> / {funnel.total_users} · {s.pct}%</span>
                              {idx > 0 && (
                                <span className={`ml-2 text-xs ${stepRet >= 70 ? 'text-[#2D6A4F]' : stepRet >= 40 ? 'text-[#B7791F]' : 'text-[#C53030]'}`}>
                                  ({stepRet}% of prev)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all"
                              style={{
                                width: `${widthPct}%`,
                                backgroundColor: idx === 0 ? '#0A1128' : idx === funnel.stages.length - 1 ? '#C5A059' : '#1F2A4A',
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#A0AABF] mt-4">
                    Use the largest gaps to prioritize UX fixes. Big drops at <strong>Onboarding → First Tool</strong> usually mean the welcome modal isn't selling value clearly. Drops at <strong>3 Tools → Consultation</strong> mean the consultation CTAs need stronger placement.
                  </p>
                </div>
              </Section>
            )}

            <Section icon={Users} title="Acquisition">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Total Users" value={data.acquisition.total_users} accent testid="stat-total-users" />
                <Stat label={`New Users (${data.window_days}d)`} value={data.acquisition.new_users_in_window} testid="stat-new-users" />
                <Stat label={`Logins (${data.window_days}d)`} value={data.acquisition.logins_in_window} testid="stat-logins" />
                <Stat label="Cohorts" value={data.acquisition.cohorts.length} sub="distinct beta codes" testid="stat-cohorts" />
              </div>
              <div className="mt-4 bg-slate-50 rounded-md p-4">
                <p className="text-xs uppercase tracking-[0.1em] text-[#4A5568] font-semibold mb-2">Cohort breakdown</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {data.acquisition.cohorts.map((c) => (
                    <div key={c.cohort} className="flex justify-between bg-white rounded p-2 text-sm">
                      <span className="text-[#4A5568]">{c.cohort}</span>
                      <span className="font-semibold ibm-plex">{c.users}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            <Section icon={Compass} title="Onboarding">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Completed" value={data.onboarding.completed} sub={`${data.onboarding.completion_rate_pct}% of users`} testid="stat-onboarding-completed" />
                <Stat label="Dismissed (no complete)" value={data.onboarding.dismissed_only} testid="stat-onboarding-dismissed" />
                <Stat label="Getting Started Views" value={data.onboarding.getting_started_views} testid="stat-getting-started-views" />
                <Stat label="Demo Video Clicks" value={data.onboarding.demo_video_clicks} testid="stat-demo-clicks" />
              </div>
            </Section>

            <Section icon={Wrench} title="Planning Tool Usage">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#C5A059] font-semibold mb-3">Most-Used Tools</p>
                  <ToolList tools={data.tools.most_used} />
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#C53030] font-semibold mb-3">Least-Used Tools</p>
                  <ToolList tools={data.tools.least_used} />
                </div>
              </div>
              <div className="bg-slate-50 rounded-md p-4">
                <p className="text-xs uppercase tracking-[0.1em] text-[#4A5568] font-semibold mb-3">All Tools</p>
                <table className="w-full text-sm" data-testid="all-tools-table">
                  <thead>
                    <tr className="text-left text-xs text-[#4A5568] uppercase tracking-[0.06em]">
                      <th className="pb-2">Tool</th>
                      <th className="pb-2 text-right">Unique Users</th>
                      <th className="pb-2 text-right">Total Uses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tools.all.map((t) => (
                      <tr key={t.tool} className="border-t border-slate-200">
                        <td className="py-2">{TOOL_LABELS[t.tool] || t.tool}</td>
                        <td className="py-2 text-right ibm-plex font-semibold">{t.unique_users}</td>
                        <td className="py-2 text-right ibm-plex">{t.total_uses}</td>
                      </tr>
                    ))}
                    {data.tools.all.length === 0 && (
                      <tr><td colSpan={3} className="py-3 text-center text-[#A0AABF]">No tool usage recorded yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section icon={Robot} title="Engagement">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Stat label="AI Coach Queries" value={data.engagement.ai_coach_queries} testid="stat-ai-queries" />
                <Stat label="AI Coach Unique Users" value={data.engagement.ai_coach_unique_users} testid="stat-ai-users" />
                <Stat label="Milestones Earned" value={data.engagement.milestones_earned} testid="stat-milestones" />
              </div>
            </Section>

            <Section icon={ChatTeardropDots} title="Feedback">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Stat label="External Form Clicks" value={data.feedback.external_button_clicks} testid="stat-feedback-clicks" />
                <Stat label="In-App Submissions" value={data.feedback.in_app_submissions} testid="stat-feedback-submissions" />
                <Stat label="Submission Rate" value={`${data.feedback.in_app_submission_rate_pct}%`} sub="in-app / total users" testid="stat-feedback-rate" />
              </div>
            </Section>

            <Section icon={Calendar} title="Consultation Conversion">
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                <Stat label="Consultation Clicks" value={data.consultation.clicks} accent testid="stat-consultation-clicks" />
                <Stat label="Click-Through Rate" value={`${data.consultation.click_through_rate_pct}%`} sub="clicks / total users" testid="stat-consultation-rate" />
              </div>
              <p className="text-xs text-[#A0AABF] mt-3">
                Actual consultation bookings require Calendly webhook integration — out of scope for beta. Track clicks as the leading indicator.
              </p>
            </Section>

            {/* ============ BETA PROGRAM (Phase 2 consumer beta) ============ */}
            <Section icon={ClipboardText} title="Beta Program">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Label className="text-xs uppercase tracking-[0.1em] text-[#4A5568]">Cohort</Label>
                <select
                  data-testid="beta-cohort-filter"
                  value={betaCohort}
                  onChange={(e) => setBetaCohort(e.target.value)}
                  className="text-sm bg-white border border-slate-300 rounded px-3 py-1.5 focus:border-[#C5A059] focus:outline-none"
                >
                  <option value="all">All participants</option>
                  <option value="phase_1">Private Beta · Phase 1</option>
                  <option value="phase_2">Private Beta · Phase 2</option>
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadBeta(adminKey, betaCohort)}
                  disabled={betaLoading}
                  data-testid="beta-refresh-button"
                  className="border-slate-300 text-[#0A1128] hover:bg-slate-50"
                >
                  <ArrowsClockwise size={14} className={`mr-1 ${betaLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  onClick={downloadBetaCsv}
                  data-testid="beta-csv-export-button"
                  className="bg-[#0A1128] hover:bg-[#152145] ml-auto"
                >
                  <Download size={14} className="mr-1" />
                  Export CSV
                </Button>
              </div>

              {betaError && <p className="text-xs text-[#B54848]">{betaError}</p>}

              {/* Beta Funnel */}
              <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="beta-funnel-panel">
                <div className="flex items-center gap-2 mb-4">
                  <FunnelSimple size={18} weight="duotone" className="text-[#C5A059]" />
                  <h3 className="cabinet-grotesk text-base font-semibold text-[#0A1128]">Beta Testing Funnel</h3>
                  <span className="text-[11px] uppercase tracking-[0.1em] text-[#A0AABF] ml-2">
                    {betaFunnel?.cohort === 'all' ? 'All cohorts' : (betaFunnel?.cohort || '').replace('_', ' · ')}
                  </span>
                </div>
                {!betaFunnel || (betaFunnel?.stages || []).every((s) => (s.count || 0) === 0) ? (
                  <p className="text-sm text-[#A0AABF]" data-testid="beta-funnel-empty">
                    No beta data yet for this cohort. Stages will populate once participants register and use PLANIVANCE AI.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {betaFunnel.stages.map((s, i) => {
                      const invited = betaFunnel.stages[0]?.count || 0;
                      const prev = i > 0 ? betaFunnel.stages[i - 1]?.count || 0 : invited;
                      const stepPct = prev ? Math.round((s.count / prev) * 100) : 0;
                      const width = Math.max(3, s.pct_of_invited || 0);
                      return (
                        <div key={s.name} className="flex items-center gap-3" data-testid={`beta-funnel-stage-${i}`}>
                          <div className="w-56 text-sm text-[#1A202C]">{s.name}</div>
                          <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden relative">
                            <div
                              className="h-full bg-[#C5A059] transition-all"
                              style={{ width: `${width}%` }}
                            />
                            <div className="absolute inset-0 flex items-center justify-end pr-3 text-xs font-semibold text-[#0A1128]">
                              {s.count} <span className="text-[#4A5568] font-normal ml-2">({s.pct_of_invited}%)</span>
                            </div>
                          </div>
                          {i > 0 && (
                            <span className="text-[11px] w-20 text-right text-[#A0AABF]" title="From previous stage">
                              {stepPct}% ↘
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Aggregate Evaluation Stats */}
              {betaAggregate && betaAggregate.count > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="beta-aggregate-panel">
                  <h3 className="cabinet-grotesk text-base font-semibold text-[#0A1128] mb-4">
                    Evaluation Results ({betaAggregate.count} submitted)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                    <Stat testid="beta-avg-navigation" label="Ease of Nav" value={betaAggregate.averages?.ease_navigation ?? '—'} sub="1–5" />
                    <Stat testid="beta-avg-next-step" label="Next-Step Clarity" value={betaAggregate.averages?.ease_next_step ?? '—'} sub="1–5" />
                    <Stat testid="beta-avg-value" label="Value Rating" value={betaAggregate.averages?.value_rating ?? '—'} sub="1–5" />
                    <Stat testid="beta-avg-reuse" label="Reuse Likelihood" value={betaAggregate.averages?.reuse_likelihood ?? '—'} sub="1–5" />
                    <Stat testid="beta-nps" label="NPS Score" value={betaAggregate.nps_score ?? '—'} sub={`avg ${betaAggregate.averages?.nps ?? '—'}`} accent />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.1em] text-[#4A5568] mb-2">Most Useful Features</h4>
                      <ul className="space-y-1 text-sm">
                        {(betaAggregate.feature_ranking || []).slice(0, 8).map(([feat, cnt]) => (
                          <li key={feat} className="flex justify-between">
                            <span className="text-[#1A202C]">{TOOL_LABELS[feat] || feat}</span>
                            <span className="ibm-plex font-semibold text-[#0A1128]">{cnt}</span>
                          </li>
                        ))}
                        {(betaAggregate.feature_ranking || []).length === 0 && (
                          <li className="text-[#A0AABF]">No selections yet</li>
                        )}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.1em] text-[#4A5568] mb-2">Preferred Access Model</h4>
                      <ul className="space-y-1 text-sm">
                        {Object.entries(betaAggregate.distributions?.preferred_access || {}).map(([k, v]) => (
                          <li key={k} className="flex justify-between">
                            <span className="text-[#1A202C] capitalize">{k.replace('_', ' ')}</span>
                            <span className="ibm-plex font-semibold text-[#0A1128]">{v}</span>
                          </li>
                        ))}
                      </ul>
                      <h4 className="text-xs uppercase tracking-[0.1em] text-[#4A5568] mt-4 mb-2">Willingness to Pay</h4>
                      <ul className="space-y-1 text-sm">
                        {Object.entries(betaAggregate.distributions?.willingness_to_pay || {}).map(([k, v]) => (
                          <li key={k} className="flex justify-between">
                            <span className="text-[#1A202C] capitalize">{k}</span>
                            <span className="ibm-plex font-semibold text-[#0A1128]">{v}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#A0AABF] mt-4">
                    Open-ended responses are stored anonymized (user ID hash only) and are included in the CSV export.
                  </p>
                </div>
              )}

              {/* Participant Status Table */}
              <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="beta-participants-panel">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <TableIcon size={18} weight="duotone" className="text-[#C5A059]" />
                  <h3 className="cabinet-grotesk text-base font-semibold text-[#0A1128]">Beta Participant Status</h3>
                  <span className="text-[11px] text-[#A0AABF] ml-2">Engagement only — no financial values shown</span>

                  <div className="ml-auto flex flex-wrap gap-2 items-center">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A0AABF]" />
                      <input
                        type="text"
                        placeholder="Search email or name"
                        value={participantSearch}
                        onChange={(e) => setParticipantSearch(e.target.value)}
                        data-testid="beta-participant-search"
                        className="text-xs pl-7 pr-2 py-1.5 border border-slate-300 rounded focus:border-[#C5A059] focus:outline-none w-56"
                      />
                    </div>
                    <select
                      value={participantEvalFilter}
                      onChange={(e) => setParticipantEvalFilter(e.target.value)}
                      data-testid="beta-participant-eval-filter"
                      className="text-xs bg-white border border-slate-300 rounded px-2 py-1.5"
                    >
                      <option value="all">Eval: all</option>
                      <option value="completed">Eval: completed</option>
                      <option value="not_completed">Eval: pending</option>
                    </select>
                    <select
                      value={participantOnbFilter}
                      onChange={(e) => setParticipantOnbFilter(e.target.value)}
                      data-testid="beta-participant-onb-filter"
                      className="text-xs bg-white border border-slate-300 rounded px-2 py-1.5"
                    >
                      <option value="all">Onboarding: all</option>
                      <option value="completed">Onboarding: completed</option>
                      <option value="pending">Onboarding: pending</option>
                    </select>
                    <select
                      value={participantSort}
                      onChange={(e) => setParticipantSort(e.target.value)}
                      data-testid="beta-participant-sort"
                      className="text-xs bg-white border border-slate-300 rounded px-2 py-1.5"
                    >
                      <option value="registered_at_desc">Newest first</option>
                      <option value="registered_at_asc">Oldest first</option>
                      <option value="last_active_at_desc">Recently active</option>
                      <option value="email_asc">Email A→Z</option>
                    </select>
                  </div>
                </div>

                {filteredParticipants.length === 0 ? (
                  <p className="text-sm text-[#A0AABF]" data-testid="beta-participants-empty">
                    No participants match the current filters.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="beta-participants-table">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#4A5568] border-b border-slate-200">
                          <th className="py-2 pr-3">Participant</th>
                          <th className="py-2 pr-3">Cohort</th>
                          <th className="py-2 pr-3">Tour</th>
                          <th className="py-2 pr-3">Onboarded</th>
                          <th className="py-2 pr-3">Modules</th>
                          <th className="py-2 pr-3">Scenario</th>
                          <th className="py-2 pr-3">Last Active</th>
                          <th className="py-2 pr-3">Evaluation</th>
                          <th className="py-2 pr-3">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredParticipants.map((p) => {
                          const onboarded = p.modules_visited_count >= 6 && p.scenario_created;
                          return (
                            <tr key={p.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`beta-participant-row-${p.user_id}`}>
                              <td className="py-2 pr-3">
                                <div className="text-[#1A202C] font-medium">{p.full_name || '—'}</div>
                                <div className="text-[11px] text-[#A0AABF]">{p.email}</div>
                              </td>
                              <td className="py-2 pr-3 text-xs text-[#4A5568] capitalize">{(p.beta_cohort || '').replace('_', ' ')}</td>
                              <td className="py-2 pr-3">
                                <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${
                                  p.tour_status === 'completed' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' :
                                  p.tour_status === 'skipped' ? 'bg-slate-100 text-[#4A5568]' :
                                  'bg-[#C5A059]/10 text-[#8B6A2F]'
                                }`}>
                                  {p.tour_status === 'not_started' ? 'not started' : p.tour_status}
                                </span>
                              </td>
                              <td className="py-2 pr-3 text-xs">
                                {onboarded ? (
                                  <span className="text-[#2D6A4F] font-semibold">yes</span>
                                ) : (
                                  <span className="text-[#A0AABF]">no</span>
                                )}
                              </td>
                              <td className="py-2 pr-3 ibm-plex text-[#0A1128] font-semibold">{p.modules_visited_count}</td>
                              <td className="py-2 pr-3 text-xs">
                                {p.scenario_created ? <span className="text-[#2D6A4F] font-semibold">yes</span> : <span className="text-[#A0AABF]">no</span>}
                              </td>
                              <td className="py-2 pr-3 text-xs text-[#4A5568]">
                                {p.last_active_at ? new Date(p.last_active_at).toLocaleDateString() : '—'}
                              </td>
                              <td className="py-2 pr-3 text-xs">
                                {p.evaluation_completed ? (
                                  <span className="text-[#2D6A4F] font-semibold">completed</span>
                                ) : (
                                  <span className="text-[#A0AABF]">pending</span>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-xs text-[#4A5568]">
                                {p.evaluation_completed_at ? new Date(p.evaluation_completed_at).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-[#A0AABF] mt-3">
                  Showing {filteredParticipants.length} of {betaParticipants.length} participants.
                  This view exposes engagement only — no income, expenses, assets, liabilities, projections, or account balances.
                </p>
              </div>
            </Section>

            <p className="text-xs text-[#A0AABF]">
              Generated at {new Date(data.generated_at).toLocaleString()} · Window: last {data.window_days} days
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
